import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_CREATE_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import type { CreateClientFormData, EditClientData } from '@/types/clientsApiTypes';
import { nullableString } from '@/utils/validations';
import { CLIENT_DETAILS_QUERY_KEY, CLIENT_QUERY_KEY } from '../../../consts/client-detail';
import { EditAddressForm } from '../../EditSectionModals/components/EditAddressForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getAddressPayload } from '../../EditSectionModals/utils/getAddressPayload';

const schema = yup.object({
  primary_passenger: yup.object().shape({
    client: yup.object().shape({
      client_mailing_address: nullableString().optional(),
      client_mailing_address_line_2: nullableString().optional(),
      client_mailing_city: nullableString().optional(),
      client_mailing_state: nullableString().optional(),
      client_mailing_country: nullableString().optional(),
      zip_code: nullableString()
        .optional()
        .max(20, 'The client zip code field must not be greater than 20 characters.'),
      is_billing_same_as_mailing: yup.boolean().optional(),
      client_billing_address: nullableString().optional(),
      client_billing_address_line_2: nullableString().optional(),
      client_billing_city: nullableString().optional(),
      client_billing_state: nullableString().optional(),
      client_billing_country: nullableString().optional(),
      billing_zip_code: nullableString().optional(),
    }),
  }),
});

export type AddressFormValues = yup.InferType<typeof schema>;

function useAddressOnSave() {
  const mutation = useEditClientMutation();
  const queryClient = useQueryClient();

  return async (payload: Partial<EditClientData>) => {
    await mutation.mutateAsync(
      {
        data: payload,
        clientId: Number(payload.client?.id),
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_QUERY_KEY] });
        },
      }
    );
  };
}

export const addressSection = defineSection<
  (typeof ModalKeys)['primary_address'],
  CreateClientFormData,
  typeof schema,
  Partial<EditClientData>
>({
  key: ModalKeys.primary_address,
  title: 'Address',
  subtitle: 'Client mailing and billing addresses on record.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: { client: c.primary_passenger.client },
  }),
  mapValuesToPayload: (v, client): Partial<EditClientData> => ({
    client: getAddressPayload({
      client_id: client?.primary_passenger.client.client_id ?? 0,
      ...v.primary_passenger.client,
    }),
  }),
  useOnSave: useAddressOnSave,
  Fields: EditAddressForm,
});